import { useEffect, useState } from 'react';
import {
  getCompanyPendingDocuments,
  getRepresentantDashboardCounters,
} from 'services/actions/representant_dashboard';
import { companyColor } from 'services/company_colors';
import { companyName } from 'services/selected_companies';
import { RepresentedTypes } from 'store/modules/profile_store/types';

export interface CompanyCount {
  id: number;
  name: string;
  color: string;
  value: number | null;
}

export type DashboardMetric = 'boletins' | 'documentos' | 'mensagens' | 'portais';

type Counts = Partial<Record<number, number | null>>;

const DOCUMENT_REQUESTS = 2;
const DOCUMENTS_CACHE_TTL = 5 * 60 * 1000;
const documentsCacheKey = (userId: number) => `representant-dashboard-documents:${userId}`;

type DocumentsCache = Partial<Record<number, { value: number; at: number }>>;

const readDocumentsCache = (userId: number): DocumentsCache => {
  try {
    return JSON.parse(sessionStorage.getItem(documentsCacheKey(userId)) || '{}');
  } catch {
    return {};
  }
};

const writeDocumentsCache = (userId: number, cache: DocumentsCache): boolean => {
  try {
    sessionStorage.setItem(documentsCacheKey(userId), JSON.stringify(cache));
    return true;
  } catch {
    return false;
  }
};

const countDocuments = async (
  ids: number[],
  userId: number,
  isActive: () => boolean,
): Promise<Counts> => {
  const cache = readDocumentsCache(userId);
  const counts: Counts = {};
  const missing = ids.filter((id) => {
    const cached = cache[id];
    if (!cached || Date.now() - cached.at >= DOCUMENTS_CACHE_TTL) return true;

    counts[id] = cached.value;
    return false;
  });

  for (let start = 0; start < missing.length && isActive(); start += DOCUMENT_REQUESTS) {
    const batch = missing.slice(start, start + DOCUMENT_REQUESTS);
    // eslint-disable-next-line no-await-in-loop
    const values = await Promise.all(
      batch.map((id) => getCompanyPendingDocuments(id).catch(() => null)),
    );
    batch.forEach((id, index) => {
      const value = values[index];
      counts[id] = value;
      if (value !== null) cache[id] = { value, at: Date.now() };
    });
  }
  writeDocumentsCache(userId, cache);

  return counts;
};

export const totalOf = (items: CompanyCount[]) =>
  items.reduce((sum, item) => sum + (item.value || 0), 0);

export const companyCounts = (
  companies: RepresentedTypes[],
  valueOf: (id: number) => number | null | undefined,
): CompanyCount[] =>
  companies.map((company, index) => ({
    id: company.id,
    name: companyName(company),
    color: companyColor(index),
    value: valueOf(company.id) ?? null,
  }));

interface Counters {
  boletins: Counts;
  mensagens: Counts;
  portais: Counts;
  documentIds: number[];
}

export function useRepresentantDashboard(companies: RepresentedTypes[], userId: number) {
  const enabled = companies.length > 1;
  const companiesKey = companies.map(({ id }) => id).join(',');
  const [counters, setCounters] = useState<Counters>();
  const [documents, setDocuments] = useState<Counts>();
  const [countersError, setCountersError] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setCountersError(false);
    getRepresentantDashboardCounters(companiesKey.split(',').map(Number))
      .then(({ data }) => {
        if (!active) return;
        const byMetric = (metric: 'boletins' | 'mensagens' | 'portais') =>
          Object.fromEntries(data.empresas.map((company) => [company.id, company[metric]]));
        setCounters({
          boletins: byMetric('boletins'),
          mensagens: byMetric('mensagens'),
          portais: byMetric('portais'),
          documentIds: data.empresas
            .filter((company) => company.gerencia_documentos)
            .map(({ id }) => id),
        });
      })
      .catch(() => active && setCountersError(true));

    return () => {
      active = false;
    };
  }, [enabled, companiesKey]);

  const documentIdsKey = counters ? counters.documentIds.join(',') : '';

  useEffect(() => {
    if (!documentIdsKey) return undefined;
    let active = true;
    countDocuments(documentIdsKey.split(',').map(Number), userId, () => active).then(
      (counts) => active && setDocuments(counts),
    );

    return () => {
      active = false;
    };
  }, [documentIdsKey, userId]);

  const breakdown = (counts: Counts, only?: number[]): CompanyCount[] =>
    companies.flatMap((company, index) =>
      only && !only.includes(company.id)
        ? []
        : [
            {
              id: company.id,
              name: companyName(company),
              color: companyColor(index),
              value: counts[company.id] ?? null,
            },
          ],
    );

  return {
    enabled,
    breakdown: {
      boletins: counters ? breakdown(counters.boletins) : [],
      documentos: counters && documents ? breakdown(documents, counters.documentIds) : [],
      mensagens: counters ? breakdown(counters.mensagens) : [],
      portais: counters ? breakdown(counters.portais) : [],
    },
    countersError,
    documentsAvailable: counters ? counters.documentIds.length > 0 : null,
    documentsError:
      countersError ||
      (!!counters &&
        !!documents &&
        counters.documentIds.every((id) => documents[id] === null || documents[id] === undefined)),
  };
}
